import { useMemo, useState } from 'react';

import { accord, pluriel } from '@/lib/pluriel';
import { PageHead } from '@/components/admin/page-head';
import { useConfirm, useToast } from '@/components/ui/feedback';
import {
  useBrands,
  useCreateBrand,
  useDeleteBrand,
  useMergeBrands,
  useUpdateBrand,
} from '@/hooks/use-catalog';
import { extractErrorMessage } from '@/hooks/use-auth-api';
import { mediaApi, type BrandDto } from '@/lib/catalog/catalog-api';

type EditTarget = 'new' | BrandDto | null;

/** Petit overlay modal (aucun composant modal partagé dans l'app). */
function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      onMouseDown={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 'min(520px, 100%)', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="card-h">
          <h3>{title}</h3>
        </div>
        <div className="card-b">{children}</div>
      </div>
    </div>
  );
}

function BrandEditModal({ target, onClose }: { target: EditTarget; onClose: () => void }) {
  const toast = useToast();
  const createMut = useCreateBrand();
  const updateMut = useUpdateBrand();
  const isNew = target === 'new';
  const brand = isNew ? null : (target as BrandDto);
  const [name, setName] = useState(brand?.name ?? '');
  const [description, setDescription] = useState(brand?.description ?? '');
  const [isActive, setIsActive] = useState(brand?.isActive ?? true);
  const [logoMediaId, setLogoMediaId] = useState<string | null>(brand?.logoMediaId ?? null);
  const [logoUrl, setLogoUrl] = useState<string | null>(brand?.logoUrl ?? null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Téléverse le logo (stocké comme média MinIO), puis le rattachera à la marque
  // à l'enregistrement via logoMediaId. Confirmation immédiate à l'utilisateur.
  const handleLogoFile = async (file: File | null) => {
    if (!file) return;
    // Vérifie le type (le glisser-déposer contourne l'attribut accept).
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/avif'].includes(file.type)) {
      setErr('Format non supporté — image PNG, JPG, WebP ou AVIF.');
      return;
    }
    setErr(null);
    setUploadingLogo(true);
    try {
      const asset = await mediaApi.upload(file, { altText: `Logo ${name}`.trim() });
      setLogoMediaId(asset.id);
      setLogoUrl(asset.url);
      toast.success('Logo téléversé.');
    } catch (e) {
      setErr(extractErrorMessage(e, 'Téléversement du logo impossible'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const save = async () => {
    setErr(null);
    try {
      if (isNew) {
        await createMut.mutateAsync({ name, description: description || null, isActive, logoMediaId });
        toast.success(`Marque « ${name.trim()} » créée.`);
      } else {
        await updateMut.mutateAsync({
          id: brand!.id,
          payload: { name, description: description || null, isActive, logoMediaId },
        });
        toast.success('Marque mise à jour.');
      }
      onClose();
    } catch (e) {
      setErr(extractErrorMessage(e, 'Enregistrement impossible'));
    }
  };

  return (
    <Modal title={isNew ? 'Nouvelle marque' : `Modifier « ${brand!.name} »`} onClose={onClose}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Nom</label>
      <input
        className="aform-i"
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Ex. Samsung"
        style={{ width: '100%', marginBottom: 12 }}
      />
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        Description (optionnel)
      </label>
      <textarea
        className="aform-i"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
        style={{ width: '100%', marginBottom: 12 }}
      />
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        Logo (optionnel)
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void handleLogoFile(e.dataTransfer.files?.[0] ?? null);
          }}
          title="Glisser une image ici ou cliquer"
          style={{
            width: 72,
            height: 72,
            flexShrink: 0,
            borderRadius: 8,
            border: dragging
              ? '2px solid var(--a-primary, #2563eb)'
              : '1px dashed var(--a-border-strong, #ccc)',
            background: dragging
              ? 'var(--a-primary-soft, rgba(37,99,235,0.06))'
              : 'var(--a-surface-2, #f6f6f6)',
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'border-color .15s, background .15s',
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo de la marque"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span
              className="muted"
              style={{ fontSize: 9, textAlign: 'center', padding: 4, lineHeight: 1.3 }}
            >
              Glisser ou cliquer
            </span>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/avif"
            style={{ display: 'none' }}
            disabled={uploadingLogo}
            onChange={(e) => void handleLogoFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <div>
          <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer', display: 'inline-flex' }}>
            {uploadingLogo ? 'Téléversement…' : logoUrl ? 'Changer le logo' : 'Téléverser un logo'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/avif"
              style={{ display: 'none' }}
              disabled={uploadingLogo}
              onChange={(e) => void handleLogoFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {logoUrl && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ marginLeft: 6 }}
              onClick={() => {
                setLogoMediaId(null);
                setLogoUrl(null);
              }}
            >
              Retirer
            </button>
          )}
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            PNG / JPG / WebP · carré, 512×512 px recommandé · 10 Mo max.
          </div>
        </div>
      </div>
      <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        <span>Marque active</span>
      </label>
      {err && <div style={{ color: 'var(--a-danger, #c00)', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onClose}>
          Annuler
        </button>
        <button
          className="btn btn-primary"
          onClick={() => void save()}
          disabled={!name.trim() || createMut.isPending || updateMut.isPending}
        >
          Enregistrer
        </button>
      </div>
    </Modal>
  );
}

function MergeModal({
  brands,
  onClose,
}: {
  brands: BrandDto[];
  onClose: () => void;
}) {
  const toast = useToast();
  const mergeMut = useMergeBrands();
  // Cible par défaut = la marque avec le plus de produits (la plus « établie »).
  const [targetId, setTargetId] = useState(
    [...brands].sort((a, b) => b.productsCount - a.productsCount)[0]?.id ?? '',
  );
  const [err, setErr] = useState<string | null>(null);
  const totalProducts = brands.reduce((s, b) => s + b.productsCount, 0);

  const doMerge = async () => {
    setErr(null);
    const sourceIds = brands.filter((b) => b.id !== targetId).map((b) => b.id);
    try {
      const res = await mergeMut.mutateAsync({ sourceIds, targetId });
      toast.success(
        `${pluriel(res.mergedFrom, 'marque')} ${accord(res.mergedFrom, 'fusionnée')} — ${pluriel(res.moved, 'produit')} ${accord(res.moved, 'rattaché')}.`,
      );
      onClose();
    } catch (e) {
      setErr(extractErrorMessage(e, 'Fusion impossible'));
    }
  };

  const target = brands.find((b) => b.id === targetId);

  return (
    <Modal title={`Fusionner ${brands.length} marques`} onClose={onClose}>
      <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Choisissez la <strong>marque à conserver</strong>. Les {totalProducts} produit(s) des autres
        lui seront rattachés, puis les doublons seront supprimés. Opération réversible produit par
        produit (le texte d'origine est conservé).
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {[...brands]
          .sort((a, b) => b.productsCount - a.productsCount)
          .map((b) => (
            <label
              key={b.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                border: '1px solid var(--a-border, #ddd)',
                borderRadius: 6,
                background: b.id === targetId ? 'var(--a-hover, #f2f7ff)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="merge-target"
                checked={b.id === targetId}
                onChange={() => setTargetId(b.id)}
              />
              <span style={{ flex: 1 }}>{b.name}</span>
              <span className="mono" style={{ opacity: 0.6, fontSize: 12 }}>
                {b.productsCount} prod.
              </span>
            </label>
          ))}
      </div>
      {target && (
        <p style={{ fontSize: 13, marginBottom: 12 }}>
          → Résultat : <strong>{target.name}</strong> avec ~{totalProducts} produit(s).
        </p>
      )}
      {err && <div style={{ color: 'var(--a-danger, #c00)', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onClose}>
          Annuler
        </button>
        <button
          className="btn btn-primary"
          onClick={() => void doMerge()}
          disabled={!targetId || mergeMut.isPending}
        >
          {mergeMut.isPending ? 'Fusion…' : 'Fusionner'}
        </button>
      </div>
    </Modal>
  );
}

export function AdminBrandsPage() {
  const [search, setSearch] = useState('');
  const { data: brands, isLoading } = useBrands(search ? { search } : {});
  const deleteMut = useDeleteBrand();
  const toast = useToast();
  const confirm = useConfirm();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [mergeOpen, setMergeOpen] = useState(false);

  const list = useMemo(() => brands ?? [], [brands]);
  const selectedBrands = useMemo(
    () => list.filter((b) => selected.has(b.id)),
    [list, selected],
  );

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  const allSelected = list.length > 0 && list.every((b) => selected.has(b.id));

  const remove = async (b: BrandDto) => {
    const ok = await confirm({
      title: `Supprimer « ${b.name} » ?`,
      message:
        b.productsCount > 0
          ? `Cette marque est rattachée à ${b.productsCount} produit(s). La suppression sera refusée — fusionnez-la plutôt.`
          : 'Cette marque n’est rattachée à aucun produit.',
      confirmLabel: 'Supprimer',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteMut.mutateAsync(b.id);
      toast.success(`Marque « ${b.name} » supprimée.`);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Suppression impossible'));
    }
  };

  return (
    <>
      <PageHead
        title="Marques"
        sub="Référentiel des marques du catalogue — fusion des doublons, activation, rattachement produits."
        actions={
          <button className="btn btn-primary" onClick={() => setEditTarget('new')}>
            + Nouvelle marque
          </button>
        }
      />

      <div className="card">
        <div className="card-h" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <input
            className="aform-i"
            placeholder="Rechercher une marque…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <span className="muted" style={{ fontSize: 12 }}>
            {pluriel(list.length, 'marque')}
          </span>
          <div style={{ flex: 1 }} />
          {selected.size >= 2 && (
            <button className="btn btn-primary" onClick={() => setMergeOpen(true)}>
              Fusionner {selected.size} marques
            </button>
          )}
          {selected.size > 0 && (
            <button className="btn btn-ghost" onClick={() => setSelected(new Set())}>
              Désélectionner
            </button>
          )}
        </div>
        <div className="card-b" style={{ padding: 0 }}>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(list.map((b) => b.id)) : new Set())
                    }
                  />
                </th>
                <th>Marque</th>
                <th>Slug</th>
                <th style={{ textAlign: 'right' }}>Produits</th>
                <th>Statut</th>
                <th style={{ width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} style={{ padding: 20, textAlign: 'center' }} className="muted">
                    Chargement…
                  </td>
                </tr>
              )}
              {!isLoading && list.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 20, textAlign: 'center' }} className="muted">
                    Aucune marque.
                  </td>
                </tr>
              )}
              {list.map((b) => (
                <tr key={b.id} style={{ background: selected.has(b.id) ? 'var(--a-hover,#f6f9ff)' : undefined }}>
                  <td>
                    <input type="checkbox" checked={selected.has(b.id)} onChange={() => toggle(b.id)} />
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          flexShrink: 0,
                          borderRadius: 4,
                          background: 'var(--a-surface-2, #f6f6f6)',
                          display: 'grid',
                          placeItems: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {b.logoUrl && (
                          <img
                            src={b.logoUrl}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        )}
                      </span>
                      {b.name}
                    </div>
                  </td>
                  <td className="mono" style={{ opacity: 0.7, fontSize: 12 }}>
                    {b.slug}
                  </td>
                  <td style={{ textAlign: 'right' }}>{b.productsCount}</td>
                  <td>
                    <span className={`tag ${b.isActive ? 'success' : 'neutral'} no-dot`}>
                      {b.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditTarget(b)}>
                        Éditer
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => void remove(b)}>
                        Suppr.
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editTarget !== null && (
        <BrandEditModal target={editTarget} onClose={() => setEditTarget(null)} />
      )}
      {mergeOpen && selectedBrands.length >= 2 && (
        <MergeModal
          brands={selectedBrands}
          onClose={() => {
            setMergeOpen(false);
            setSelected(new Set());
          }}
        />
      )}
    </>
  );
}
